import { api } from './api';
import {getAssetUrl} from "../../components/Public/utils";
import {mapJobData} from "./jobApi";


export const mapCompanyData = (companyResponse: any) => {
  if (!companyResponse) return null;
  return {
    id: companyResponse.id,
    name: companyResponse.name || "Unnamed Company",
    description: companyResponse.description || "No description available.",
    logo: getAssetUrl(companyResponse.logo_file?.attachment?.url),
    website: companyResponse.website,
    email: companyResponse.email,
    phone: companyResponse.phone,
    sector: companyResponse.sector,
    location: [companyResponse.location?.region?.name, companyResponse.location?.country?.name].filter(Boolean).join(", ") || "Ethiopia",
    jobs: companyResponse.jobs?.map(mapJobData),
    isSupplier: Boolean(companyResponse.isSupplier),
  }
}

export const companyApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get companies waiting for identity verification
    getUnverifiedCompanies: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetUnverifiedCompanies {
              companies(where: {
                OR: [
                  { identity_verification_status: { status: { equals: "pending" } } },
                  { identity_verification_status: null }
                ]
              }) {
                id
                name
                email
                phone
                website
                description
                logo_file {
                  id
                  attachment {
                    url
                    filename
                  }
                }
                identity_verification_status {
                  id
                  status
                }
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data.companies || [],
      providesTags: ['Company'],
    }),

    // Get all companies with pagination
    getCompanies: builder.query({
      query: ({
        take = 20,
        skip = 0,
        searchText
      }: {
        take?: number;
        skip?: number;
        searchText?: string;
      } = {}) => {
        const whereClause: any = {};

        if (searchText && searchText.trim()) {
          whereClause.OR = [
            { name: { contains: searchText, mode: "insensitive" } },
            { description: { contains: searchText } }
          ];
        }

        return {
          url: '',
          method: 'POST',
          body: {
            query: `
              query GetCompanies($where: CompanyWhereInput!, $take: Int, $skip: Int!) {
                companies(
                  where: $where
                  orderBy: { created_at: desc }
                  take: $take
                  skip: $skip
                ) {
                  id
                  name
                  email
                  phone
                  website
                  description
                  created_at
                  logo_file {
                    id
                    attachment {
                      url
                      filename
                    }
                  }
                  location {
                    id
                    country { id name }
                    region { id name }
                  }
                  identity_verification_status {
                    id
                    status
                  }
                }
              }
            `,
            variables: { where: whereClause, take, skip },
          },
        };
      },
      transformResponse: (response: any) => response.data.companies || [],
      providesTags: ['Company'],
    }),

    // Get companies count for pagination
    getCompaniesCount: builder.query({
      query: ({ searchText }: { searchText?: string } = {}) => {
        const whereClause: any = {};

        if (searchText && searchText.trim()) {
          whereClause.OR = [
            { name: { contains: searchText, mode: 'insensitive' } },
            { description: { contains: searchText } }
          ];
        }

        return {
          url: '',
          method: 'POST',
          body: {
            query: `
              query GetCompaniesCount($where: CompanyWhereInput!) {
                companiesCount(where: $where)
              }
            `,
            variables: { where: whereClause },
          },
        };
      },
      transformResponse: (response: any) => response.data.companiesCount,
      providesTags: ['Company'],
    }),
    getCompanyUserByUserId: builder.query({
      query: (userId) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetMyCompanyUser($userId: ID!) {
              companyUsers(where: { user: { id: { equals: $userId } } }) {
                id
                company {
                  id
                  name
                }
              }
            }
          `,
          variables: { userId },
        },
      }),
      transformResponse: (response) => {
        const companyUser = response.data.companyUsers[0];
        return {
          id: companyUser.id || "",
          company: mapCompanyData(companyUser.company)
        }
      },
    }),

    // Helper query to get status IDs if needed (though connect by status name is preferred)
    getVerificationStatuses: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetVerificationStatuses {
              identityVerificationStatuses {
                id
                status
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data.identityVerificationStatuses || [],
    }),
    // Get single company by ID
    getCompanyById: builder.query({
      query: (id) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetCompanyById($id: ID!) {
              company(where: { id: $id }) {
                id
                name
                email
                phone
                website
                description
                created_at
                logo_file {
                  id
                  attachment {
                    url
                    filename
                  }
                }
                location {
                  id
                  country { id name }
                  region { id name }
                }
                identity_verification_status {
                  id
                  status
                }
                company_user {
                  user {
                    id
                  }
                }
              }
            }
          `,
          variables: { id },
        },
      }),
      transformResponse: (response: any) => response.data.company,
      providesTags: (result, error, id) => [{ type: 'Company', id: id as string }],
    }),
    // Get users for a specific company
    getCompanyUsers: builder.query({
      query: (companyId) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetCompanyUsers($companyId: ID!) {
              companyUsers(where: { company: { id: { equals: $companyId } } }) {
                id
                user {
                  id
                  full_name
                  email
                  phone
                  account_status {
                    status
                  }
                  role { name }
                }
              }
            }
          `,
          variables: { companyId },
        },
      }),
      transformResponse: (response: any) => response.data.companyUsers || [],
      providesTags: ['CompanyUser'],
    }),

    registerEmployer: builder.mutation<
      { userId: string; companyId: string },
      {
        company: {
          name: string,
          description?: string,
          location?: {
            countryId: string,
            regionId: string,
          }
        };
        fullName: string;
        email: string;
        phone: string;
        gender: string;
        password: string;
      }
    >({
      async queryFn(args, _api, _extra, baseQuery) {
        const { location } = args.company;

        const result: any = await baseQuery({
          url: '',
          method: 'POST',
          body: {
            query: `
              mutation RegisterEmployerAccount(
                $fullName: String!
                $email: String!
                $phone: String
                $gender: String!
                $password: String!
                $companyName: String!
                $description: String
                $countryId: ID
                $regionId: ID
              ) {
                registerEmployerAccount(
                  fullName: $fullName
                  email: $email
                  phone: $phone
                  gender: $gender
                  password: $password
                  companyName: $companyName
                  description: $description
                  countryId: $countryId
                  regionId: $regionId
                ) {
                  userId
                  companyId
                  companyUserId
                }
              }
            `,
            variables: {
              fullName: args.fullName,
              email: args.email,
              phone: args.phone,
              gender: args.gender,
              password: args.password,
              companyName: args.company.name,
              description: args.company.description,
              countryId: location?.countryId,
              regionId: location?.regionId,
            },
          },
        });

        if (result.error) return { error: { status: 'CUSTOM_ERROR',  data: { errors: result.error.data.errors } }};

        const payload = result.data?.data?.registerEmployerAccount;
        if (!payload?.companyId) {
          return { error: { status: 'CUSTOM_ERROR', data: { errors: result.data?.errors || [{ message: 'Employer registration failed' }] } } };
        }

        return {
          data: {
            userId: payload.userId,
            companyId: payload.companyId,
            companyUserId: payload.companyUserId,
          },
        }
      },
      invalidatesTags: ['User', 'Company', 'CompanyUser'],
    }),

    // Add user to company
    addUserToCompany: builder.mutation({
      query: ({ companyId, userId }) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation AddUserToCompany($companyId: ID!, $userId: ID!) {
              createCompanyUser(data: {
                company: { connect: { id: $companyId } },
                user: { connect: { id: $userId } }
              }) {
                id
              }
            }
          `,
          variables: { companyId, userId },
        },
      }),
      invalidatesTags: ['CompanyUser'],
    }),

    // Remove user from company
    removeUserFromCompany: builder.mutation({
      query: (companyUserId) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation RemoveUserFromCompany($id: ID!) {
              deleteCompanyUser(where: { id: $id }) {
                id
              }
            }
          `,
          variables: { id: companyUserId },
        },
      }),
      invalidatesTags: ['CompanyUser'],
    }),

    // Get all users (for searching/adding to company)
    getUsers: builder.query({
      query: (searchTerm) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetUsers($searchTerm: String) {
              users(where: {
                OR: [
                  { full_name: { contains: $searchTerm } },
                  { email: { contains: $searchTerm } }
                ]
              }) {
                id
                full_name
                email
                role { name }
              }
            }
          `,
          variables: { searchTerm },
        },
      }),
      transformResponse: (response: any) => response.data.users || [],
    }),

    // Update user detail as admin (Name, Email, Password, Account Status)
    updateUserAdmin: builder.mutation({
      query: ({ userId, data }) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation UpdateUserAdmin($id: ID!, $data: UserUpdateInput!) {
              updateUser(where: { id: $id }, data: $data) {
                id
                full_name
                email
                account_status {
                  id
                  status
                }
              }
            }
          `,
          variables: { id: userId, data },
        },
      }),
      invalidatesTags: ['CompanyUser', 'User'],
    }),

    exportCompanies: builder.query<Record<string, unknown>[], void>({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query ExportCompanies {
              exportCompanies
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data?.exportCompanies || [],
    }),

    // Get account statuses
    getAccountStatuses: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetAccountStatuses {
              accountStatuses {
                id
                status
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data.accountStatuses || [],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useGetCompanyUsersQuery,
  useGetCompaniesCountQuery,
  useGetCompanyUserByUserIdQuery,
  useRegisterEmployerMutation,
  useUpdateUserAdminMutation,
  useLazyExportCompaniesQuery,
} = companyApi;
